import type { ApiDailyRewardGrant } from "@bridge-applications/pb-types";
import { useCallback, useEffect, useRef } from "react";
import { playManagedSound, type ManagedSoundPlayback } from "../helpers/audio";
import { dailyRewardSounds } from "../helpers/dailyRewardCollection";
import { REWARD_COLLECTION_TIMING } from "../helpers/rewardCollection";

const REWARD_SOUND_FADE_MS = 150;
const MIXED_REWARD_SOUND_STAGGER_MS = 120;
const MIXED_REWARD_SOUND_VOLUME = 0.7;

export function useDailyRewardCollectionSound(
    grants: readonly ApiDailyRewardGrant[],
) {
    const playbacksRef = useRef<ManagedSoundPlayback[]>([]);
    const fadeSound = useCallback(() => {
        for (const playback of playbacksRef.current)
            playback.cancel(REWARD_SOUND_FADE_MS);
    }, []);

    useEffect(() => {
        const sounds = dailyRewardSounds(grants);
        if (sounds.length === 0) return;

        const playbacks = sounds.map((sound, index) =>
            playManagedSound(sound, {
                delayMs:
                    REWARD_COLLECTION_TIMING.revealDelayMs +
                    index * MIXED_REWARD_SOUND_STAGGER_MS,
                volume: sounds.length > 1 ? MIXED_REWARD_SOUND_VOLUME : 1,
                fadeOutMs: REWARD_SOUND_FADE_MS,
                // Like Inbox claims, never queue a stale cue in a suspended context.
                getOffsetSeconds: () => 0,
            }),
        );
        playbacksRef.current = playbacks;
        const cancel = () => {
            for (const playback of playbacks) playback.cancel(0);
        };
        const onVisibilityChange = () => {
            if (document.hidden) cancel();
        };
        window.addEventListener("blur", cancel);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            cancel();
            if (playbacksRef.current === playbacks) playbacksRef.current = [];
            window.removeEventListener("blur", cancel);
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
        };
    }, [grants]);

    return fadeSound;
}
